<template>
  <div id="app-container">
    <div v-if="isLoadingGame" class="loading-overlay">
      <p>山札を準備しています...</p>
    </div>

    <DeckBuilder v-if="isBuildingDeck" @start-game="startGameWithConstructedDeck" />
    <div id="app" v-else>
      <header class="game-header">
        <h1>ラブカ Solo</h1>
        <div class="controls">
          <button class="action-button return-builder-button" @click="returnToDeckBuilder">構築画面に戻る</button>
          <button class="action-button reset-button" @click="resetGame">リセット</button>
          <button class="action-button" @click="drawCardToHand">山札を1枚ドロー</button>
          <button class="action-button" @click="drawCardToWaitingRoom">山札を1枚控え室へ</button>
          <button class="action-button" @click="drawCardToResolutionArea">山札を1枚解決領域へ</button>
          <button class="action-button undo-button" @click="undoLastAction" :disabled="!previousState">1回戻る</button>
        </div>
      </header>

      <main class="game-board" @scroll="handleBoardScroll">
        <div class="board-inner">
          <div class="left-column">
            <div class="successful-live-area-container">
              <h2>成功ライブ</h2>
              <draggable class="card-area successful-live" v-model="successfulLiveArea" group="cards" item-key="id"
                :force-fallback="true" delay="150" :delay-on-touch-only="true" :move="checkMove">
                <template #item="{ element, index }">
                  <div class="card-list-item" :style="{
                    zIndex: index,
                    position: 'relative'
                  }">
                    <Card :card="element" :area-type="'resolutionArea'" />
                  </div>
                </template>
              </draggable>
            </div>
            <div class="area-action">
              <button class="action-button" @click="returnResolutionAreaToTopOfDeck">解決領域を山札の上へ</button>
              <button class="action-button" @click="returnResolutionAreaToDeckAndShuffle">解決領域を山札へ(シャッフル)</button>
              <button class="action-button" @click="moveResolutionAreaToWaitingRoom">解決領域を全て控え室へ</button>
            </div>
            <div class="resolution-area-container">
              <h2>解決領域</h2>
              <draggable class="card-area resolution-area" v-model="resolutionArea" group="cards" item-key="id"
                @click="showOverlay('resolutionArea')" :force-fallback="true" delay="150" :delay-on-touch-only="true"
                :move="checkMove">
                <template #item="{ element, index }">
                  <div class="card-list-item" :style="{
                    zIndex: index,
                    position: 'relative'
                  }">
                    <Card :card="element" :area-type="'resolutionArea'" />
                  </div>
                </template>
              </draggable>
            </div>
          </div>

          <div class="center-column">
            <div class="center-column-main">
              <div class="live-group">
                <div class="area-header">
                  <button class="area-header-button" @click="isLiveAreaHidden = !isLiveAreaHidden">{{ isLiveAreaHidden ?
                    '表示' :
                    '隠す' }}</button>
                  <h2>ライブ置き場</h2>
                  <button class="area-header-button primary" @click="startLive">ライブ開始</button>
                </div>
                <div class="card-slots" :class="{ 'is-hidden': isLiveAreaHidden }">
                  <draggable class="card-area live-slot" v-model="liveArea.left" group="cards" item-key="id"
                    :force-fallback="true" @add="handleCardAdded($event, liveArea.left)" :move="checkMove">
                    <template #item="{ element }">
                      <Card :card="element" :area-type="'live'" />
                    </template>
                  </draggable>
                  <draggable class="card-area live-slot" v-model="liveArea.center" group="cards" item-key="id"
                    :force-fallback="true" @add="handleCardAdded($event, liveArea.center)" :move="checkMove">
                    <template #item="{ element }">
                      <Card :card="element" :area-type="'live'" />
                    </template>
                  </draggable>
                  <draggable class="card-area live-slot" v-model="liveArea.right" group="cards" item-key="id"
                    :force-fallback="true" @add="handleCardAdded($event, liveArea.right)" :move="checkMove">
                    <template #item="{ element }">
                      <Card :card="element" :area-type="'live'" />
                    </template>
                  </draggable>
                </div>
              </div>
              <div class="stage-group">
                <h2>ステージ</h2>
                <div class="card-slots">
                  <draggable class="card-area stage-slot" v-model="stage.left" group="cards" item-key="id"
                    :force-fallback="true" delay="150" :delay-on-touch-only="true"
                    @add="handleCardAdded($event, stage.left)" @start="onStackDragStart($event, stage.left)"
                    @end="onStackDragEnd($event, stage.left)" :move="checkMove">
                    <template #item="{ element, index }">
                      <div class="card-list-item" :style="{
                        zIndex: index,
                        position: 'relative'
                      }">
                        <Card :card="element" :area-type="'stage'" @card-clicked="toggleCardOrientation(element)" />
                      </div>
                    </template>
                  </draggable>
                  <draggable class="card-area stage-slot" v-model="stage.center" group="cards" item-key="id"
                    :force-fallback="true" delay="150" :delay-on-touch-only="true"
                    @add="handleCardAdded($event, stage.center)" @start="onStackDragStart($event, stage.center)"
                    @end="onStackDragEnd($event, stage.center)" :move="checkMove">
                    <template #item="{ element, index }">
                      <div class="card-list-item" :style="{
                        zIndex: index,
                        position: 'relative'
                      }">
                        <Card :card="element" :area-type="'stage'" @card-clicked="toggleCardOrientation(element)" />
                      </div>
                    </template>
                  </draggable>
                  <draggable class="card-area stage-slot" v-model="stage.right" group="cards" item-key="id"
                    :force-fallback="true" delay="150" :delay-on-touch-only="true"
                    @add="handleCardAdded($event, stage.right)" @start="onStackDragStart($event, stage.right)"
                    @end="onStackDragEnd($event, stage.right)" :move="checkMove">
                    <template #item="{ element, index }">
                      <div class="card-list-item" :style="{
                        zIndex: index,
                        position: 'relative'
                      }">
                        <Card :card="element" :area-type="'stage'" @card-clicked="toggleCardOrientation(element)" />
                      </div>
                    </template>
                  </draggable>
                </div>
              </div>
            </div>
            <div class="area-header">
              <button class="area-header-button" @click="isHandHidden = !isHandHidden">{{ isHandHidden ? '開く' : '閉じる'
              }}</button>
              <h2>手札</h2>
              <div class="area-header-placeholder"></div>
            </div>
            <draggable class="card-area hand" v-show="!isHandHidden" v-model="hand" group="cards" item-key="id"
              :force-fallback="true" :move="checkMove">
              <template #item="{ element }">
                <Card :card="element" :area-type="'hand'" />
              </template>
            </draggable>

            <div class="energy-area-container">
              <div class="area-header">
                <button class="action-button" @click="changeEnergyCount(-1)"
                  :disabled="energyArea.length <= 0">-</button>
                <span>&nbsp; {{ energyArea.length }} / 12 &nbsp;</span>
                <button class="action-button" @click="changeEnergyCount(1)"
                  :disabled="energyArea.length >= 12">+</button>
                <h2>エネルギー</h2>
                <div class="area-header-placeholder-2"></div>
              </div>
              <draggable class="card-area energy-area" v-model="energyArea" group="cards" item-key="id"
                :force-fallback="true" :move="checkMove">
                <template #item="{ element, index }">
                  <div class="card-list-item" :style="{
                    zIndex: index,
                    position: 'relative'
                  }">
                    <Card :card="element" :area-type="'energy'" @card-clicked="toggleCardOrientation(element)" />
                  </div>
                </template>
              </draggable>
            </div>
          </div>

          <div class="right-column">
            <div class="deck-area">
              <h2>山札</h2>
              <div class="card-area deck-slot" @click="showOverlay('deck')">
                <div v-if="deck.length > 0" class="card-back"><span class="card-count">{{ deck.length }}</span></div>
              </div>
              <button class="action-button" @click="refreshDeck">リフレッシュ</button>
            </div>
            <div class="waiting-room-area">
              <h2>控え室</h2>
              <draggable class="card-area waiting-room" v-model="waitingRoom" group="cards" item-key="id"
                @click="showOverlay('waitingRoom')" :force-fallback="true" delay="150" :delay-on-touch-only="true"
                :move="checkMove">
                <template #item="{ element, index }">
                  <div class="card-list-item" :style="{
                    zIndex: index,
                    position: 'relative'
                  }">
                    <Card :card="element" :area-type="'waitingRoom'" />
                  </div>
                </template>
              </draggable>
            </div>
          </div>
        </div>
      </main>

      <footer class="game-footer" :class="{ 'is-hidden': isScrolling }">
        <p>このサイトは、一個人が作成した非公式のファンサイトです。作品の著作権は、権利所有者に帰属します。<br>作成者: <a href="https://twitter.com/nmanju_"
            target="_blank" rel="noopener noreferrer">大蒜饅頭</a></p>
      </footer>

      <div v-if="isOverlayVisible" class="overlay" :class="{ 'is-dragging': isDraggingFromOverlay }">
        <div class="overlay-content">
          <button class="close-button" @click="closeOverlay">&times;</button>
          <h2>{{ overlayTitle }}</h2>
          <draggable class="overlay-card-list" v-model="overlayCards"
            :group="{ name: 'cards', pull: isOverlayDraggable }" :disabled="!isOverlayDraggable"
            :class="{ 'no-drag': !isOverlayDraggable }" item-key="id" :force-fallback="true" @start="onOverlayDragStart"
            @end="onOverlayDragEnd">
            <template #item="{ element }">
              <Card :card="element" :area-type="overlayAreaType" />
            </template>
          </draggable>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';

import Card from './components/Card.vue';
import DeckBuilder from './components/DeckBuilder.vue';

import {
  ENERGY_CARD_LIMIT,
  INITIAL_ENERGY_COUNT,
  LOCAL_DATA_URL,
  REMOTE_DATA_URL,
  CARD_TYPE_MEMBER,
  CARD_TYPE_LIVE,
  CARD_TYPE_ENERGY,
  ENERGY_CARD_ID,
  ENERGY_CARD_NAME,
  ENERGY_CARD_IMG
} from './constants';

export default {
  name: 'App',
  components: {
    Card,
    DeckBuilder,
    draggable,
  },
  data() {
    return {
      isLoadingGame: false,
      isBuildingDeck: true, // 最初はデッキ構築画面を表示
      masterDeck: [],
      deck: [],
      stage: { left: [], center: [], right: [] },
      liveArea: { left: [], center: [], right: [] },
      successfulLiveArea: [],
      waitingRoom: [],
      hand: [],
      resolutionArea: [],
      energyArea: [],
      draggedStack: null,
      isOverlayVisible: false,
      overlayTitle: '',
      overlayCards: [],
      isDraggingFromOverlay: false,
      overlayAreaType: null,
      isHandHidden: false,
      isLiveAreaHidden: false,
      isScrolling: false,
      scrollTimeout: null,
      previousState: null,
    };
  },
  computed: {
    /**
     * すべてのエリアにカードが存在しないかを判定します。
     * @returns {boolean} 盤面が空の場合はtrue
     */
    isBoardEmpty() {
      // 配列で管理されているエリアのチェック (デッキ以外)
      const arrayAreas = [this.hand, this.waitingRoom, this.successfulLiveArea, this.resolutionArea];
      const arraysAreEmpty = arrayAreas.every(area => area.length === 0);
      if (!arraysAreEmpty) {
        return false; // いずれかの配列にカードがあれば、盤面は空ではない
      }

      // オブジェクトで管理されているエリアのチェック
      const objectAreas = [...Object.values(this.stage), ...Object.values(this.liveArea)];
      const objectsAreEmpty = objectAreas.every(slot => slot === null);

      // 配列もオブジェクトもすべて空の場合のみtrueを返す
      return objectsAreEmpty;
    },
    isOverlayDraggable() {
      // 山札のオーバーレイではドラッグを無効化する
      return this.overlayAreaType !== 'deck';
    },
  },
  beforeUnmount() {
    // コンポーネントが破棄される時にタイマーをクリア
    clearTimeout(this.scrollTimeout);
  },
  created() {
    // createdフックでエネルギーの初期化処理を呼び出す
    this.initializeEnergy();
  },
  methods: {
    async startGameWithConstructedDeck() {
      this.isLoadingGame = true;

      try {
        const savedDeckData = localStorage.getItem('llocg_deck');
        if (!savedDeckData) {
          alert('デッキデータが見つかりません。');
          return;
        }

        const deckJson = JSON.parse(savedDeckData);
        await this.initializeDeckFromBuilder(deckJson);
        this.isBuildingDeck = false; // 一人回し画面に切り替え
      } catch (error) {
        // エラーが発生した場合もアラートなどを表示
        console.error("Failed to start game:", error);
        alert("ゲームの開始に失敗しました。");
      } finally {
        this.isLoadingGame = false; // 成功・失敗にかかわらずローディング終了
      }
    },

    async initializeDeckFromBuilder(deckData) {
      // 全カード情報を取得して、カード名などを参照できるようにします
      const localUrl = LOCAL_DATA_URL;
      const remoteUrl = REMOTE_DATA_URL;

      try {
        // まずローカルのカードDBのみを読み込みます
        const localResponse = await fetch(localUrl);
        if (!localResponse.ok) throw new Error('ローカルのカードDBの読み込みに失敗しました。');
        const localData = await localResponse.json();

        // デッキ内の全カードがローカルDBに存在するかチェックします
        const deckCardIds = Object.keys(deckData);
        const allCardsInLocal = deckCardIds.every(id => Object.prototype.hasOwnProperty.call(localData, id));

        let combinedData = localData;

        // もしローカルに存在しないカードが1枚でもあれば、remoteUrlも読み込みます
        if (!allCardsInLocal) {
          console.log("Remote fetch is necessary. Fetching remote data..."); // デバッグ用
          const remoteResponse = await fetch(remoteUrl);
          if (!remoteResponse.ok) throw new Error('外部のカードDBの読み込みに失敗しました。');
          const remoteData = await remoteResponse.json();
          // 2つのJSONオブジェクトを1つにマージ
          // 注: 同じIDのカードが存在する場合、後のlocalDataの内容で上書きされます
          combinedData = { ...remoteData, ...localData };
        } else {
          console.log("Remote fetch skipped. All cards found in local data."); // デバッグ用
        }

        // 結合したデータを一度フラットなカードオブジェクトの配列に変換します
        const allCardsArray = Object.values(combinedData);

        // 高速に検索できるよう、カードIDをキーにしたMapオブジェクトを生成します
        const cardLookupMap = new Map();
        for (const card of allCardsArray)
          // 'card_no'プロパティを持つ有効なカードデータのみをMapに登録
          if (card && card.card_no)
            cardLookupMap.set(card.card_no, card);

        const newDeck = [];

        // deckDataオブジェクトをループ処理し、枚数分のカードを生成
        for (const cardId in deckData) {
          const count = deckData[cardId];
          const cardInfo = cardLookupMap.get(cardId);

          if (cardInfo) {
            for (let i = 0; i < count; i++) {
              newDeck.push({
                id: `${cardId}-${i}`,
                name: cardInfo.name,
                img: cardInfo.img,
                type: cardInfo.type,
                isRotated: false,
              });
            }
          } else {
            // もしカードが見つからなかった場合にコンソールに警告を出す（デバッグ用）
            console.warn(`Card with ID "${cardId}" not found in the database.`);
          }
        }

        this.resetBoard(); // deck以外の盤面をリセット

        this.masterDeck = [...newDeck]; // シャッフル前の状態をマスターとして保存
        this.deck = this.shuffleArray(newDeck); // シャッフルしたものを現在の山札にセット
      } catch (error) {
        alert(error.message);
        console.error("Failed to initialize deck:", error);
      }
    },

    resetBoard() {
      this.deck = [];
      this.stage = { left: [], center: [], right: [] };
      this.liveArea = { left: [], center: [], right: [] };
      this.successfulLiveArea = [];
      this.waitingRoom = [];
      this.hand = [];
      this.resolutionArea = [];
    },

    shuffleArray(array) {
      for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
      }
      return array;
    },

    async resetGame() {
      if (window.confirm('現在の盤面をすべてリセットし、山札を再生成します。よろしいですか？')) {
        this.saveStateForUndo(); // 操作前に状態を保存
        this.resetBoard(); // まず盤面をクリア
        // 保存しておいたマスターデータから新しい山札を生成してシャッフル
        this.deck = this.shuffleArray([...this.masterDeck]);
        alert('盤面をリセットしました。');
      }
    },

    returnToDeckBuilder() {
      // 盤面が空でない場合にのみ、状態を保存する
      if (!this.isBoardEmpty) {
        this.saveStateForUndo();
      }

      this.isBuildingDeck = true; // 構築画面に切り替え
    },

    drawCard(targetArea, count = 1) {
      if (this.deck.length < count) {
        alert(`山札が足りません！ 残り: ${this.deck.length}枚`);
        return;
      }
      const cardsToDraw = this.deck.splice(0, count);
      cardsToDraw.forEach(card => this[targetArea].push(card));
    },

    drawCardToHand() {
      if (this.deck.length > 0) this.drawCard('hand');
      else alert('山札がありません！');
    },

    drawCardToWaitingRoom() {
      if (this.deck.length > 0) this.drawCard('waitingRoom');
      else alert('山札がありません！');
    },

    refreshDeck() {
      if (this.waitingRoom.length === 0) {
        alert('控え室にカードがありません。');
        return;
      }
      this.saveStateForUndo(); // 操作前に状態を保存
      const numCards = this.waitingRoom.length;
      this.deck.push(...this.waitingRoom);
      this.waitingRoom = [];
      this.deck = this.shuffleArray(this.deck);
      alert(`控え室のカード${numCards}枚を山札に戻してシャッフルしました。`);
    },

    /**
     * 山札から1枚カードを引き、解決領域に送ります。
     */
    drawCardToResolutionArea() {
      if (this.deck.length > 0) {
        this.drawCard('resolutionArea');
      } else {
        alert('山札がありません！');
      }
    },

    /**
     * 解決領域のカードを、順番を維持したまま山札の上に戻します。
     */
    returnResolutionAreaToTopOfDeck() {
      if (this.resolutionArea.length === 0) {
        alert('解決領域にカードがありません。');
        return;
      }
      this.saveStateForUndo(); // 操作前に状態を保存
      const numCards = this.resolutionArea.length;
      // 解決領域のカードを山札の配列の先頭に追加
      this.deck.unshift(...this.resolutionArea);
      this.resolutionArea = []; // 解決領域を空にする
      alert(`解決領域のカード${numCards}枚を山札の上に戻しました。`);
    },

    /**
     * 解決領域のカードを山札に戻し、シャッフルします。
     */
    returnResolutionAreaToDeckAndShuffle() {
      if (this.resolutionArea.length === 0) {
        alert('解決領域にカードがありません。');
        return;
      }
      this.saveStateForUndo(); // 操作前に状態を保存
      const numCards = this.resolutionArea.length;
      // 解決領域のカードを山札の配列の末尾に追加
      this.deck.push(...this.resolutionArea);
      this.resolutionArea = []; // 解決領域を空にする
      this.deck = this.shuffleArray(this.deck); // 山札をシャッフル
      alert(`解決領域のカード${numCards}枚を山札に戻してシャッフルしました。`);
    },

    moveResolutionAreaToWaitingRoom() {
      if (this.resolutionArea.length === 0) {
        alert('解決領域にカードがありません。');
        return;
      }
      this.saveStateForUndo(); // 操作前に状態を保存
      const numCards = this.resolutionArea.length;
      // 解決領域のカードを控え室の配列に追加
      this.waitingRoom.push(...this.resolutionArea);
      // 解決領域を空にする
      this.resolutionArea = [];
      alert(`解決領域のカード${numCards}枚を控え室に送りました。`);
    },

    startLive() {
      let movedCount = 0;
      const hasMemberCard = Object.values(this.liveArea).some(slotArray => slotArray.length > 0 && slotArray[0].type === CARD_TYPE_MEMBER);
      if (!hasMemberCard) {
        alert('ライブ置き場にメンバーカードが見つかりませんでした。');
        return;
      }
      for (const slotKey in this.liveArea) {
        if (this.liveArea[slotKey].length > 0 && this.liveArea[slotKey][0].type === CARD_TYPE_MEMBER) {
          this.waitingRoom.push(...this.liveArea[slotKey]); // 控え室に送る
          this.liveArea[slotKey] = []; // ライブ置き場から削除
          movedCount++;
        }
      }
      if (movedCount > 0) {
        alert(`ライブ置き場のメンバーカード${movedCount}枚を控え室に送りました。`);
      }
    },

    // カードの移動ルールをチェック
    checkMove(evt) {
      const toClasses = evt.to.classList;
      const draggedCard = evt.draggedContext.element;
      const targetList = evt.relatedContext.list;

      // エネルギー置き場への移動
      if (toClasses.contains('energy-area'))
        // 「エネルギー」カードであり、かつ12枚未満の場合のみ許可
        return draggedCard.type === CARD_TYPE_ENERGY && targetList.length < ENERGY_CARD_LIMIT;

      // エネルギーカードの移動
      if (draggedCard.type === CARD_TYPE_ENERGY) {
        if (toClasses.contains('stage-slot'))
          // ステージにメンバーカードが存在する場合のみドロップを許可
          return targetList.some(card => card.type === CARD_TYPE_MEMBER);
        // エネルギーカードは、エネルギー置き場とステージ以外には移動できない
        return false;
      }

      // ステージスロットへの移動
      if (toClasses.contains('stage-slot')) {
        if (draggedCard.type === CARD_TYPE_LIVE)
          return false; // ライブカードは移動禁止
        // ステージへのドロップを常に一旦許可し、複雑な判定はhandleCardAddedに任せる
        return true;
      }

      // 複数枚置けるエリアは常に許可
      if (toClasses.contains('hand') || toClasses.contains('waiting-room') ||
        toClasses.contains('resolution-area') || toClasses.contains('successful-live')) {
        return true;
      }

      // ライブ置き場の場合は、2枚まで許容（ドロップ直後の処理で1枚に戻すため）
      return evt.relatedContext.list.length < 2;
    },

    /**
     * ステージやライブ置き場にカードが追加された際の処理。
     * スロットに2枚目のカードが入ったら、元々あったカードを控え室に送ります。
     */
    handleCardAdded(evt, targetSlot) {
      // エネルギーカードがステージに置かれた場合の処理を追加
      const newCard = targetSlot[evt.newIndex];

      // 安全のため、カード情報が取得できなかった場合は処理を中断
      if (!newCard) return;

      // エネルギーカードがステージにドロップされた場合の処理
      if (newCard.type === CARD_TYPE_ENERGY) {
        // 常にエネルギーカードが配列の先頭に来るように並べ替える
        const energyCards = targetSlot.filter(c => c.type === CARD_TYPE_ENERGY);
        const memberCard = targetSlot.find(c => c.type === CARD_TYPE_MEMBER);

        targetSlot.length = 0; // スロットを一度空にする
        // エネルギーカード、メンバーカードの順で配列を再構築
        if (memberCard)
          Array.prototype.push.apply(targetSlot, [...energyCards, memberCard]);
        return; // 通常の上書き処理は行わない
      }

      // メンバーカードがステージにドロップされた場合の処理
      if (newCard.type === CARD_TYPE_MEMBER) {
        // 置き換えられる古いメンバーカードを探す
        const oldMemberCard = targetSlot.find(c => c.type === CARD_TYPE_MEMBER && c.id !== newCard.id);

        if (oldMemberCard) {
          // 古いメンバーカードが見つかった場合（＝上書き発生時）
          // 1. 古いメンバーカードを控え室に送る
          this.waitingRoom.push(oldMemberCard);

          // 2. スロットを、新しいメンバーカードのみの状態にする
          //    (古いメンバーカードと、それに付随するエネルギーカードはすべて消える)
          targetSlot.length = 0;
          targetSlot.push(newCard);
        }
      }

      // スロットにカードが2枚以上ある場合（＝上書きが発生した場合）
      if (targetSlot.length > 1) {
        // evt.newIndex は、新しく追加されたカードのインデックス
        // スロットには2枚しかないので、古いカードのインデックスは 1 - evt.newIndex で求められる
        const oldCardIndex = 1 - evt.newIndex;
        const oldCard = targetSlot[oldCardIndex];

        if (oldCard) {
          // 古いカードを控え室に送る
          this.waitingRoom.push(oldCard);

          // スロットから古いカードを削除して、新しいカードだけを残す
          targetSlot.splice(oldCardIndex, 1);
        }
      }

      // もし「かたまり」がドラッグされてきて、追加されたのがそのメンバーカードなら
      if (this.draggedStack && newCard.id === this.draggedStack.member.id) {
        // 移動先が「ステージ」である場合のみ
        if (evt.to.classList.contains('stage-slot'))
          // 記憶しておいたエネルギーカードを、メンバーの前にまとめて追加する
          targetSlot.splice(0, 0, ...this.draggedStack.energy);
      }
    },

    // メンバーとエネルギーをかたまりでドラッグする処理
    onStackDragStart(evt, sourceArray) {
      this.draggedStack = null; // 念のためリセット
      const draggedItem = sourceArray[evt.oldIndex];

      // ドラッグしたのがメンバーカードの場合のみ処理
      if (draggedItem && draggedItem.type === CARD_TYPE_MEMBER) {
        const energyCards = sourceArray.filter(card => card.type === CARD_TYPE_ENERGY);
        // 重なっているエネルギーカードがあれば、かたまりとして記憶
        if (energyCards.length > 0) {
          this.draggedStack = {
            member: draggedItem,
            energy: energyCards,
          };
        }
      }
    },

    onStackDragEnd(evt, sourceArray) {
      // もし「かたまり」をドラッグしていたなら、移動元に残ったエネルギーを掃除する
      if (this.draggedStack && evt.from !== evt.to) {
        const remaining = sourceArray.filter(c => c.type !== CARD_TYPE_ENERGY);
        sourceArray.length = 0;
        sourceArray.push(...remaining);

        // 次のドラッグ操作のためにリセット
        this.draggedStack = null;
      }
    },

    /**
     * カードがクリックされたときに、そのカードの回転状態を切り替えます。
     */
    toggleCardOrientation(clickedCard) {
      // isRotatedプロパティの真偽値を反転させます
      clickedCard.isRotated = !clickedCard.isRotated;
    },

    // オーバーレイからのドラッグ開始
    onOverlayDragStart() {
      this.isDraggingFromOverlay = true;
    },

    // オーバーレイからのドラッグ終了
    onOverlayDragEnd(evt) {
      this.isDraggingFromOverlay = false;
      // カードが別のリスト（オーバーレイの外）に移動されたかチェック
      if (evt.to !== evt.from) {
        // vuedraggableのv-modelがoverlayCardsからカードを削除してくれる。
        // 元の配列からも同じカードを削除する必要がある。
        const movedCardId = evt.item.getAttribute('data-id'); // Cardコンポーネントにdata-id属性を追加する必要がある
        const originalArray = this[this.overlayAreaType];
        const cardIndex = originalArray.findIndex(card => card.id === movedCardId);
        if (cardIndex > -1) {
          originalArray.splice(cardIndex, 1);
        }
        this.closeOverlay();
      }
    },

    /**
     * カード一覧オーバーレイを表示します。
     * @param {string} areaName - 'waitingRoom','resolutionArea','deck'
     */
    showOverlay(areaName) {
      this.overlayAreaType = areaName;
      if (areaName === 'deck') {
        if (this.deck.length === 0) { alert('山札にカードがありません。'); return; }
        this.overlayTitle = '山札の内容';
        this.overlayCards = [...this.deck].sort((a, b) => {
          const isALive = a.type === CARD_TYPE_LIVE; const isBLive = b.type === CARD_TYPE_LIVE;
          if (isALive && !isBLive) return 1; if (!isALive && isBLive) return -1;
          return a.id.localeCompare(b.id, undefined, { numeric: true });
        });
      } else {
        if (this[areaName].length === 0) return;
        this.overlayTitle = { 'waitingRoom': '控え室', 'resolutionArea': '解決領域' }[areaName] || 'カード一覧';
        this.overlayCards = [...this[areaName]];
      }
      this.isOverlayVisible = true;
    },
    /**
     * カード一覧オーバーレイを閉じます。
     */
    closeOverlay() {
      this.isOverlayVisible = false;
      this.overlayTitle = '';
      this.overlayCards = [];
      this.overlayAreaType = null;
    },

    handleBoardScroll() {
      // スクロールが始まったら isScrolling を true にする
      this.isScrolling = true;

      // 既存のタイマーがあればクリアする
      clearTimeout(this.scrollTimeout);

      // 2000ミリ秒後にスクロールが停止したと判断し、isScrolling を false に戻す
      this.scrollTimeout = setTimeout(() => {
        this.isScrolling = false;
      }, 2000);
    },

    saveStateForUndo() {
      // JSONを介してディープコピーを作成し、現在の状態を保存
      this.previousState = JSON.parse(JSON.stringify({
        deck: this.deck,
        stage: this.stage,
        liveArea: this.liveArea,
        successfulLiveArea: this.successfulLiveArea,
        waitingRoom: this.waitingRoom,
        hand: this.hand,
        resolutionArea: this.resolutionArea,
      }));
    },

    // 状態を元に戻す
    undoLastAction() {
      if (!this.previousState) {
        alert('元に戻せる操作がありません。');
        return;
      }

      // 保存しておいた状態を現在の状態に復元
      this.deck = this.previousState.deck;
      this.stage = this.previousState.stage;
      this.liveArea = this.previousState.liveArea;
      this.successfulLiveArea = this.previousState.successfulLiveArea;
      this.waitingRoom = this.previousState.waitingRoom;
      this.hand = this.previousState.hand;
      this.resolutionArea = this.previousState.resolutionArea;

      // 一度使ったら戻せる状態をnullに戻す（1回しか戻れないようにするため）
      this.previousState = null;

      alert('直前の操作を元に戻しました。');
    },

    /**
     * エネルギー置き場を初期化します。デフォルトで3枚配置します。
     */
    initializeEnergy() {
      this.energyArea = []; // 一旦リセット
      const energyCardId = ENERGY_CARD_ID;

      for (let i = 0; i < INITIAL_ENERGY_COUNT; i++) {
        this.energyArea.push({
          id: `${energyCardId}-${i}`,
          card_no: energyCardId,
          name: ENERGY_CARD_NAME,
          img: ENERGY_CARD_IMG,
          type: CARD_TYPE_ENERGY,
          isRotated: false,
        });
      }
    },

    /**
     * エネルギーカードの枚数を変更します。
     * @param {number} amount - 変更量 (+1 または -1)
     */
    changeEnergyCount(amount) {
      if (amount > 0 && this.energyArea.length < ENERGY_CARD_LIMIT) {
        const energyCardId = ENERGY_CARD_ID;
        // 既存のカードと重複しない、ユニークなIDを生成します
        const newUniqueId = `${energyCardId}-${Date.now()}-${Math.random()}`;

        this.energyArea.push({
          id: newUniqueId,
          card_no: energyCardId,
          name: ENERGY_CARD_NAME,
          img: ENERGY_CARD_IMG,
          type: CARD_TYPE_ENERGY,
          isRotated: false,
        });
      } else if (amount < 0 && this.energyArea.length > 0) {
        this.energyArea.pop();
      }
    },
  }
}
</script>

<style>
html,
body {
  height: 100%;
  margin: 0;
  overflow: hidden;
  /* ページ全体のスクロールを禁止 */
}

h1 {
  margin: 10px;
}

h2 {
  margin: 0 0 10px 0;
  font-size: 1em;
  text-align: center;
  letter-spacing: 1px;
}

a:link {
  color: #8AB4F8;
}

a:visited {
  color: #C58AF9;
}

:root {
  --board-bg: #34495e;
  --area-bg: rgba(0, 0, 0, 0.2);
  --area-border: #7f8c8d;
  --header-bg: #2c3e50;
  --text-light: #ecf0f1;
}

#app-container {
  font-family: 'Helvetica Neue', Arial, 'Hiragino Kaku Gothic ProN', 'Hiragino Sans', Meiryo, sans-serif;
  height: 100%;
}

#app {
  color: var(--text-light);
  background-color: var(--board-bg);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.loading-overlay {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  font-size: 1.5em;
  color: var(--text-light, #ecf0f1);
}

.game-header {
  padding: 10px 20px;
  background-color: var(--header-bg);
  border-bottom: 2px solid var(--area-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 10px;
  position: sticky;
  /* ヘッダーを画面上部に固定 */
  top: 0;
  z-index: 1000;
  /* 他の要素より手前に表示 */
}

.controls {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
}

.action-button {
  padding: 4px 10px;
  border: none;
  background-color: #4a6fa5;
  color: var(--text-light);
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  font-size: 0.9em;
  white-space: normal;
  text-align: center;
}

.action-button:hover {
  background-color: #5a82c4;
}

.action-button:disabled {
  background-color: #7f8c8d;
  cursor: not-allowed;
  opacity: 0.2;
}

.action-button.return-builder-button {
  background-color: #e63946;
  /* DeckBuilderのボタンと同じ色に */
}

.action-button.return-builder-button:hover {
  background-color: #f2525e;
  /* ホバー時の色 */
}

.action-button.reset-button {
  background-color: #8e44ad;
}

.action-button.reset-button:hover {
  background-color: #9b59b6;
}

.action-button.undo-button {
  background-color: #f39c12;
}

.action-button.undo-button:hover {
  background-color: #f5b041;
}

.game-board {
  padding: 20px;
  padding-bottom: 50px;
  /* フッターの高さ分(少し余裕を持つ)の余白を追加 */
  flex-grow: 1;
  overflow-x: auto;
  overflow-y: auto;
  min-height: 0;
}

.board-inner {
  display: grid;
  grid-template-columns: 186px 1fr 136px;
  gap: 20px;
  min-width: 800px;
  min-height: 750px;
}

.left-column,
.right-column {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.center-column {
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-height: 0;
}

.center-column-main {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.card-area {
  background-color: var(--area-bg);
  border: 2px dashed var(--area-border);
  border-radius: 10px;
  position: relative;
  width: 100%;
  min-height: 126px;
  /* 最小高さを保証 */
  display: flex;
  justify-content: center;
  align-items: center;
  transition: background-color 0.2s;
}

.successful-live-area-container {
  display: flex;
  flex-direction: column;
  flex-grow: 0;
  /* 伸縮しない */
  flex-shrink: 0;
  /* 縮まない */
}

.waiting-room-area,
.resolution-area-container {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  /* 残りのスペースを伸縮して埋める */
}

.live-group,
.stage-group {
  display: flex;
  flex-direction: column;
}

.card-slots {
  display: flex;
  justify-content: center;
  gap: 20px;
  width: 100%;
}

.stage-slot {
  width: 126px;
  height: 176px;
  overflow: visible;
  /* はみ出したカードを表示 */
  display: flex;
  align-items: center;
  /* 子要素を横方向の中央に配置 */
  justify-content: flex-start;
  /* 縦方向を上寄せに明示 */
  position: relative;
  /* 子要素を配置する基準に */
}

.live-slot {
  width: 176px;
  height: 126px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/*
  ライブ置き場にカードをドラッグした際のゴーストのスタイルを上書きします。
  position:absolute を使い、ゴーストをスロットいっぱいに広げることで、
  元々あったカードを完全に覆い隠し（上書きし）、表示を統一します。
*/
.live-slot .sortable-ghost {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  /* ネガティブマージン等の影響をリセット */
  z-index: 1000;
  /* 元のカードより確実に手前に表示 */
  background-color: rgba(255, 255, 255, 0.2);
}

.deck-area {
  flex-shrink: 0;
}

.deck-slot {
  width: 126px;
  height: 176px;
  margin: 0 auto 10px auto;
  cursor: pointer;
}

.card-back {
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 2px solid var(--text-light);
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
}

.card-count {
  font-size: 1.5em;
  font-weight: bold;
}

.hand {
  width: 99%;
  min-height: calc(176px + 20px);
  padding: 10px;
  gap: 8px;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  align-content: flex-end;
}

.area-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.area-header h2 {
  margin: 0;
  flex-grow: 1;
  text-align: center;
}

.area-header-button {
  padding: 4px 10px;
  border: 1px solid var(--area-border);
  background-color: transparent;
  color: var(--text-light);
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8em;
  transition: background-color 0.2s, color 0.2s;
  white-space: nowrap;
  width: 86px;
  text-align: center;
}

.area-header-button:hover {
  background-color: var(--area-border);
  color: var(--header-bg);
}

.area-header-button.primary {
  border-color: #4a6fa5;
  background-color: #4a6fa5;
}

.area-header-button.primary:hover {
  background-color: #5a82c4;
  color: var(--text-light);
}

.area-header-placeholder {
  width: 86px;
}

.area-header-placeholder-2 {
  width: 126px;
}

.card-slots.is-hidden .card-item {
  box-shadow: none;
  /* 隠れているときは影を消す */
}

/* ::after擬似要素を使って白いオーバーレイをカードの上に重ねる */
.card-slots.is-hidden .card-item::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ffffff;
  /* 真っ白な背景 */
  border-radius: 6px;
  /* Card.vueの角丸に合わせる */
  border: 1px solid #ccc;
  /* 白いカードの枠線 */
  z-index: 1000;
  /* 画像の上に表示 */
}

.successful-live.card-area {
  display: flex;
  flex-direction: column;
  /* 子要素を縦方向に並べる */
  align-items: center;
  /* 子要素を横方向の中央に配置 */
  justify-content: flex-start;
  /* 縦方向を上寄せに明示 */
  padding-top: 5px;
  /* 上の余白を5pxに設定 */
  padding-left: 5px;
  /* 左右の余白も維持 */
  padding-right: 5px;
  width: 191px;
  height: 241px;
  padding: 5px;
  margin: 0 auto;
  overflow: hidden;
  box-sizing: border-box;
}

.waiting-room-area .card-area,
.resolution-area-container .card-area {
  min-height: 200px;
  flex-grow: 1;
  position: relative;
  padding: 5px;
}

.waiting-room.card-area,
.resolution-area.card-area {
  display: flex;
  /* flexコンテナとして定義 */
  flex-direction: column;
  /* 子要素を縦方向に並べる */
  align-items: center;
  /* 子要素を横方向の中央に配置 */
  justify-content: flex-start;
  /* 縦方向を上寄せに明示 */
  padding-top: 5px;
  /* 上の余白を5pxに設定 */
  width: 191px;
  height: auto;
  box-sizing: border-box;
}

.waiting-room.card-area {
  width: 141px;
}

.resolution-area.card-area {
  width: 191px;
}

/* まず、全アイテムのデフォルトマージンを0に設定 */
.card-list-item {
  margin-top: 0;
}

/* 2番目以降の要素（通常カードまたはゴースト）にマージンを適用 */
.stage-slot .card-list-item+.card-list-item,
.stage-slot .sortable-ghost+.card-list-item,
.stage-slot .card-list-item+.sortable-ghost {
  margin-left: -146px;
}

.successful-live .card-list-item+.card-list-item,
.successful-live .sortable-ghost+.card-list-item,
.successful-live .card-list-item+.sortable-ghost {
  margin-top: -76px;
}

.resolution-area .card-list-item+.card-list-item,
.resolution-area .sortable-ghost+.card-list-item,
.resolution-area .card-list-item+.sortable-ghost {
  margin-top: -106px;
}

.waiting-room .card-list-item+.card-list-item,
.waiting-room .sortable-ghost+.card-list-item,
.waiting-room .card-list-item+.sortable-ghost {
  margin-top: -156px;
}

.area-action {
  flex-shrink: 0;
  /* ボタンが縮まないように */
  display: flex;
  flex-direction: column;
  gap: 5px;
}

/* 盤面上のアクションボタンは横幅いっぱいに広げる */
.area-action .action-button,
.deck-area .action-button {
  width: 100%;
}

/* オーバーレイのスタイル */
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  padding: 20px;
  transition: background-color 0.2s ease-in-out;
}

.overlay.is-dragging {
  /* 背景を完全な透明に */
  background-color: transparent;
  /* マウスイベントを背後の要素に透過させる */
  pointer-events: none;
}

.overlay-content {
  background-color: var(--board-bg);
  border: 2px solid var(--area-border);
  border-radius: 10px;
  padding: 20px 40px;
  width: 100%;
  max-width: 1200px;
  height: 100%;
  max-height: 90vh;
  position: relative;
  display: flex;
  flex-direction: column;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.5);
  transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out;
  pointer-events: auto;
}

.overlay-content h2 {
  font-size: 1.5em;
  text-align: center;
  margin-bottom: 20px;
  flex-shrink: 0;
}

.overlay.is-dragging .overlay-content {
  /* オーバーレイの内容は見えなくする */
  opacity: 0;
  visibility: hidden;
}

.close-button {
  position: absolute;
  top: 10px;
  right: 15px;
  background: none;
  border: none;
  color: var(--text-light);
  font-size: 2.5em;
  line-height: 1;
  cursor: pointer;
  padding: 0;
}

.overlay-card-list {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 10px;
  overflow-y: auto;
  flex-grow: 1;
  justify-content: center;
  align-content: flex-start;
}

/*
  山札のオーバーレイなど、ドラッグが無効になっているエリアの
  カードカーソルを標準（デフォルト）に戻します。
*/
.overlay-card-list.no-drag .card-item,
.overlay-card-list.no-drag .card-item:active {
  cursor: default;
}

.game-footer {
  position: fixed;
  /* ← 文書フローから切り離し、画面に固定 */
  bottom: 0;
  /* ← 画面下部に配置 */
  left: 0;
  right: 0;
  z-index: 1001;
  /* ← ヘッダー(1000)より手前に表示 */
  padding: 10px 20px;
  text-align: center;
  color: var(--area-border);
  font-size: 0.8em;
  background-color: var(--header-bg);
  border-top: 1px solid var(--area-border);
  transition: opacity 0.5s ease, transform 0.5s ease;
  transform: translateY(0);
}

.game-footer p {
  margin: 0;
}

.game-footer.is-hidden {
  opacity: 0;
  transform: translateY(100%);
  /* 下にスライドアウトする効果 */
  pointer-events: none;
  /* 非表示中はクリックできないようにする */
}

/* vuedraggable用のヘルパースタイル */
.card-area .sortable-ghost {
  opacity: 0.4;
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 6px;
}

/* ドラッグ中に生成されるクローン要素のスタイル */
.sortable-drag {
  z-index: 9999 !important;
  /* 他のどの要素よりも手前に表示 */
}

.sortable-fallback {
  opacity: 1 !important;
}

.card-area.stage-slot>div {
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
}

.energy-area-container {
  margin-top: 20px;
}

.energy-area {
  height: 196px;
  /* 領域の高さを固定 */
  display: flex;
  justify-content: center;
  /* カードのスタックを水平方向中央に配置 */
  align-items: center;
  /* カードのスタックを垂直方向中央に配置 */
  overflow: hidden;
  /* 高さを超えたカードを隠す */
}

.energy-area .card-list-item+.card-list-item {
  margin-left: -46px;
}

/* 画面の幅が768px以下の場合に適用されます */
@media (max-width: 768px) {
  .game-header {
    /* ヘッダー全体の上下の余白を少し狭くします */
    padding-top: 8px;
    padding-bottom: 8px;
    /* 狭い画面では要素が縦に並んでも良いようにします */
    flex-direction: column;
  }

  .game-header h1 {
    /* タイトルの文字サイズを少し小さくします */
    font-size: 1.3em;
  }

  .controls {
    /* ボタン群を中央揃えにします */
    justify-content: center;
  }

  .action-button {
    /* ボタンの文字と余白を少し小さくします */
    font-size: 0.8em;
    padding: 6px 10px;
  }
}

/* さらに小さい画面（例：480px以下）用の設定 */
@media (max-width: 480px) {
  .game-header h1 {
    font-size: 1.2em;
  }

  .controls {
    /* ボタンが1列に収まらない場合に折り返すようにします */
    flex-wrap: wrap;
  }
}
</style>
